import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useCreateAidStation } from '../../hooks/useAidStations'
import { useSegments } from '../../hooks/useSegments'
import { validateAidStationDistance } from '../../lib/calculations'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Textarea } from '../ui/textarea'

const aidStationSchema = z.object({
  name: z.string().min(1, 'Name is required').max(100, 'Name too long'),
  at_distance_km: z.number().min(0, 'Distance must be positive'),
  planned_stop_min: z.number().min(0).max(300).optional(),
  available: z.string().optional(),
  notes: z.string().optional(),
})

type AidStationForm = z.infer<typeof aidStationSchema>

interface AddAidStationDialogProps {
  runId: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function AddAidStationDialog({ runId, open, onOpenChange }: AddAidStationDialogProps) {
  const [error, setError] = useState<string | null>(null)
  const createMutation = useCreateAidStation()
  const { data: segments } = useSegments(runId)
  
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<AidStationForm>({
    resolver: zodResolver(aidStationSchema),
    defaultValues: {
      planned_stop_min: 0
    }
  })


  const onSubmit = async (data: AidStationForm) => {
    setError(null)

    // Validate distance
    if (segments) {
      const validation = validateAidStationDistance(data.at_distance_km, segments)
      if (!validation.isValid) {
        setError(validation.message || 'Invalid distance')
        return
      }
    }

    try {
      const availableItems = data.available
        ? data.available.split(',').map(item => item.trim()).filter(Boolean)
        : []

      await createMutation.mutateAsync({
        run_id: runId,
        name: data.name,
        at_distance_km: data.at_distance_km,
        planned_stop_min: data.planned_stop_min || 0,
        available: availableItems,
        notes: data.notes || null,
      })

      reset()
      onOpenChange(false)
    } catch (err) {
      setError('Failed to create aid station')
    }
  }

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      reset()
      setError(null)
    }
    onOpenChange(newOpen)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Add Aid Station</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              {...register('name')}
              placeholder="e.g., Mile 20 Aid Station"
            />
            {errors.name && (
              <p className="text-sm text-red-500">{errors.name.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="at_distance_km">Distance (km)</Label>
            <Input
              id="at_distance_km"
              type="number"
              step="0.1"
              {...register('at_distance_km', { valueAsNumber: true })}
              placeholder="32.2"
            />
            {errors.at_distance_km && (
              <p className="text-sm text-red-500">{errors.at_distance_km.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="planned_stop_min">Planned Stop (minutes)</Label>
            <Input
              id="planned_stop_min"
              type="number"
              {...register('planned_stop_min', { valueAsNumber: true })}
              placeholder="5"
            />
            {errors.planned_stop_min && (
              <p className="text-sm text-red-500">{errors.planned_stop_min.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="available">Available Items (comma-separated)</Label>
            <Input
              id="available"
              {...register('available')}
              placeholder="Water, Sports drink, Gels, Bananas"
            />
            <p className="text-xs text-gray-500">
              List items available at this aid station, separated by commas
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              {...register('notes')}
              placeholder="Any additional notes about this aid station..."
              rows={3}
            />
          </div>

          {error && (
            <div className="text-sm text-red-500 bg-red-50 p-2 rounded">
              {error}
            </div>
          )}

          <div className="flex justify-end space-x-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={createMutation.isPending}
            >
              {createMutation.isPending ? 'Adding...' : 'Add Aid Station'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}